// The coach panel (spec §13.15). It reads everything from the v2 client: the
// thread items the stream carried
// (one renderer per type, ThreadItem.jsx), the coach's text as it streams
// (`coach.text`) in a live bubble until its coach_message lands, the header's
// status line (`run.status`), and the athlete's own messages while they are
// still being sent.
//
// - All / Coach actions: the coach log is GET /v2/thread?filter=coach (older
//   history) merged with what the stream has carried since.
// - Day dividers; it opens at the first item the athlete hasn't seen (the last
//   seq seen is kept on this device).
// - The newest safety item is pinned at the top as a one-line banner (expands
//   to the card) until acknowledged on this device or 7 days old.
// - Needs you: the decisions waiting on the athlete (open proposals, open
//   intake questions) are the LAST block of the list, just above the composer,
//   out of their place in the history until answered; the panel opens at the
//   bottom on them, and WaitingBar ("Review ↓") shows while they're scrolled
//   out of view.
// - Chips above the composer (v2/suggestions.js): GET /v2/thread/suggestions whenever
//   the athlete's state offers any, else the quiet thread's prompts. A coach message's
//   body.followups show under it while it is the newest.
// - The composer carries a context chip (Ask why): a Why? on an item, a safety
//   card's "Talk to your coach", or a session's Ask why (`chat:why`) sends the
//   next message with that `context_ref`.
//
// M4: intake questions, the strategy proposal card and the working item of a
// run writing a plan. The coach button (and the tab title) badge the coach
// items after the last seq read on this device plus the open proposals
// (coachBadgeCount). Onboarding opens
// the thread on arrival (OPEN_KEY, read by every panel mounted until the
// athlete closes it or ARRIVAL_TTL_MS pass) with the first plan's working item
// already showing (BUILD_KEY: "Getting started on your plan…" until the
// run's first step, then its steps and findings, then the draft's).
//
// Every v2 athlete sees the whole thread and answers it (Accept, Try again,
// Build my plan, intake chips) whatever their tier: only typing to the coach
// (the composer, the suggested prompts) is Pro+ (allowed('chat')).
//
// Cutover: a human coach viewing a v2 athlete sees this panel read-only
// (useV2().readOnly): the thread as the athlete sees it, with no composer, no
// suggested prompts and no buttons that send (accept, answer, undo, Ask why).
// An athlete with a human coach gets that coach's direct messages here too
// (HumanCoachThread).
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightToLine, ChevronRight, Eraser, Eye, Maximize2, MessageCircle, Sparkle, X } from 'lucide-react';
import { Button, Chip, EmptyState, FOCUS, SegmentedControl } from '../ds';
import { cn } from '../../lib/utils';
import Composer from '../chat/Composer';
import HumanCoachThread from '../chat/HumanCoachThread';
import { v2API } from '../../services/api';
import { useEntitlement } from '../../context/AuthContext';
import { useCoaching } from '../../context/CoachingContext';
import { useV2, useV2State } from '../../context/V2Context';
import { threadItems } from '../../v2/reducer';
import { commands } from '../../v2/commands';
import { REBUILDING_TEXT, canRetry } from '../../v2/failures';
import { planBuildAsked } from '../../v2/calendarRoutes';
import { creationView } from '../../v2/strategy';
import { isRebuild, shouldSend } from '../../v2/rebuildConfirm';
import { useConfirm } from '../ui/confirm';
import {
  ADD_RACE_KEY, startingView, readArrival, clearArrival, waitsOnAthlete, contextLabel, filterItems, firstUnreadId, isQuiet, localDay,
  badgedTitle, coachBadgeCount, mergeCoachLog, pinnedSafety, statusLine, withDayDividers, isRetiredOffer,
  needsYouItems, waitingItems, REVIEW_EVENT, focusThreadItem, blockInView, afterClear, canClearChat, OPEN_SESSION_EVENT, OPEN_SESSION_KEY,
} from '../../v2/threadView';
import { composerChips, composerPlaceholder, followupChips, latestCoachId, objectLabel, readSuggestions } from '../../v2/suggestions';
import { aboutRemovedActivity, knownRemoved, onActivitiesChanged } from '../../lib/activityChanges';
import WaitingBar from './WaitingBar';
import SuggestionChips from './SuggestionChips';
import SyncStatus from './SyncStatus';
import CreationWork from './CreationWork';
import { PinnedSafety } from './SafetyCard';
import ThreadItem, { AthleteText, CoachText } from './ThreadItem';
import CoachActionRow from './CoachActionRow';
import CoachResize from './CoachResize';
import { StepList } from './ReplyExtras';

const ActivityDetailModal = lazy(() => import('../ActivityDetailModal'));

const VIEW_KEY = 'aithlo:chatView';
const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === 'fullscreen' ? 'fullscreen' : 'sidebar'; } catch { return 'sidebar'; }
};

const readSidebarCollapsed = () => {
  try {
    const saved = localStorage.getItem('aithlo:sidebarCollapsed');
    return saved === null ? window.innerWidth < 1024 : saved === '1';
  } catch { return false; }
};

// Per-device memory (provisional: no read marker or acknowledge endpoint yet).
const readJSON = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); } catch { return fallback; }
};
const writeJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode: it is remembered for this visit */ }
};

const FILTERS = [['all', 'All'], ['coach', 'Actions']];

/** `defaultOpen`: start with the panel open (tests; Layout mounts it closed).
 * `embedded`: the thread inside the page that holds it (onboarding's first
 * plan, FirstPlanScreen): always open, filling its box, no close or expand. */
export default function CoachThread({ defaultOpen = false, embedded = false }) {
  const { client, me, readOnly = false, api = v2API } = useV2();
  const state = useV2State();
  const { coaches = [], coachUnreadTotal = 0, fetchRelationships, viewingAthlete = null } = useCoaching() || {};
  const humanCoaches = useMemo(() => (readOnly ? [] : coaches), [readOnly, coaches]);
  const navigate = useNavigate();
  // An unknown tier (whoami not answered yet, e.g. the API was unreachable) keeps
  // the composer; only a KNOWN tier without chat gets "Subscribe to keep your coach".
  const { denied } = useEntitlement();
  const chatOpen = !denied('chat');
  const userId = me?.user_id || 'me';
  const READ_KEY = `aithlo:v2:${userId}:readSeq`;
  const ACK_KEY = `aithlo:v2:${userId}:safetyAck`;
  const SEEN_KEY = `aithlo:v2:${userId}:safetySeen`;

  // Onboarding's hand-off (handOffToCoach) opens it from the first render, in
  // every panel mounted while it stands: one that is remounted is open too.
  const [isOpen, setIsOpen] = useState(() => embedded || defaultOpen || readArrival(null).open);
  const [view, setView] = useState(readView);
  // Expanded, the coach sits beside the app's sidebar: follow its width (Sidebar.jsx's sidebar:layout).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  useEffect(() => {
    const onSidebar = (e) => setSidebarCollapsed(Boolean(e.detail?.collapsed));
    window.addEventListener('sidebar:layout', onSidebar);
    return () => window.removeEventListener('sidebar:layout', onSidebar);
  }, []);
  const [input, setInput] = useState('');
  const [context, setContext] = useState(null);         // { ref, label }: what the next message asks about
  const [suggestions, setSuggestions] = useState([]);   // GET /v2/thread/suggestions
  const [activitiesSeen, setActivitiesSeen] = useState(0); // bumped when an activity is removed, merged or restored
  const [activity, setActivity] = useState(null);       // the activity a reply's source or card opened
  // Activities known to be gone (removed or merged here, or a 404 on opening one
  // an older reply names): those replies' cards and sources say "Removed activity".
  const [removedActivities, setRemovedActivities] = useState(() => new Set());
  const [filter, setFilter] = useState('all');
  const [coachLog, setCoachLog] = useState(null);       // GET /v2/thread?filter=coach's items
  const [logError, setLogError] = useState(false);
  const [acked, setAcked] = useState(() => readJSON(ACK_KEY, []));
  const [anchor, setAnchor] = useState(null);           // the first unread item when the panel opened
  const [mode, setMode] = useState('ai');               // 'ai' | a human coach's id
  const [readSeq, setReadSeq] = useState(() => readJSON(READ_KEY, null));   // the last seq read on this device
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const endRef = useRef(null);
  const wide = !embedded && view === 'fullscreen';
  const now = Date.now();
  const today = localDay();

  const items = useMemo(() => (state.cal ? afterClear(threadItems(state.cal)).filter((x) => !isRetiredOffer(x)) : []), [state.cal]);
  // A build that failed (plan_failed / plan_not_applied) is over: no spinner (#66).
  const creation = useMemo(() => creationView(state.cal, { outcomes: state.outcomes, pending: state.pending }),
    [state.cal, state.outcomes, state.pending]);
  const races = useMemo(() => Object.values(state.cal?.races || {}), [state.cal?.races]);
  // A coach viewing an athlete gets no badge for the athlete's items.
  const badge = readOnly ? 0 : coachBadgeCount(state.cal, readSeq, state.pending);
  const activeCoach = mode === 'ai' ? null : humanCoaches.find((c) => c.id === mode) || null;
  // A rebuild asked for (Regenerate Plan, a "plan for me" ask, onboarding's first plan): the coach is on it until its run ends.
  const rebuilding = !readOnly && planBuildAsked(state.pending, state.outcomes);
  // Before the draft starts, the asked-for build's run (its first status-only
  // run) shows as the working item too, so it reads as one card from arrival.
  const startingRun = rebuilding && (!creation || creation.done)
    ? Object.entries(state.live || {}).find(([, r]) => !r.text)?.[0] || null : null;
  const starting = rebuilding && (!creation || creation.done) && !waitsOnAthlete(items, state.pending) ? startingView(startingRun ? state.cal?.work?.[startingRun] : null) : null;
  // The run writing a plan shows as its working item, not as a bare status line.
  const runs = Object.entries(state.live || {}).filter(([run]) => run !== creation?.run_id && run !== startingRun);
  const sending = state.pending.filter((c) => c.op?.kind === 'message' && c.status !== 'confirmed');
  // Refused messages stay in the thread under their bubble: Not sent · Try again · Remove.
  const unsent = (state.failed || []).filter((f) => f.op?.kind === 'message');
  const acting = new Set(state.pending.filter((c) => c.op?.kind === 'item_state').map((c) => String(c.op.id)));
  // A second full rebuild today asks first (src/v2/rebuildConfirm.js), a Refresh that rebuilds too;
  // every other command goes at once.
  const confirm = useConfirm();
  const act = (cmd) => {
    if (!isRebuild(cmd)) return client?.send(cmd);
    shouldSend(cmd, { readMe: () => v2API.me().then((r) => r.data), confirm })
      .then((go) => { if (go) client?.send(cmd); });
    return true;
  };
  const clearChat = async () => {
    if (await confirm({ title: 'Clear this chat?', confirmText: 'Clear chat',
      description: 'You and your coach start fresh from here. Your plan, Coach actions and what your coach has noted about you stay.' })) {
      client?.send(commands.clearChat());
    }
  };
  const pinned = pinnedSafety(items, acked, now);
  // Needs you (Figma "Docked coach panel"): the coach's open proposals and open
  // questions, the last block of the thread (newest last) until they're
  // answered, out of their place in the history above.
  const needsYou = useMemo(() => (filter === 'all' ? needsYouItems(items, state.pending) : []), [filter, items, state.pending]);
  // Every card still waiting on the athlete (the block less those whose answer
  // is on its way): the bar above the composer names them while the block is
  // out of view. Not for a coach viewing the athlete.
  const waiting = useMemo(() => (readOnly ? [] : waitingItems(items, state.pending)), [readOnly, items, state.pending]);
  const waitingRef = useRef(waiting);
  waitingRef.current = waiting;
  // The card to bring into view: { id, smooth, highlight, n } (n: each ask anew).
  const [focus, setFocus] = useState(null);
  const [nudge, setNudge] = useState(0);              // a new card arrived off screen: the bar says so
  // Whether the Needs-you block is in view (the bar hides while it is).
  const [blockEl, setBlockEl] = useState(null);
  const [needsYouInView, setNeedsYouInView] = useState(false);
  useEffect(() => {
    if (!blockEl || typeof IntersectionObserver === 'undefined') { setNeedsYouInView(false); return undefined; }
    const io = new IntersectionObserver(([entry]) => setNeedsYouInView(blockInView(entry)),
      { root: listRef.current, threshold: [0, 0.1, 0.25, 0.5, 0.6, 0.75, 1] });
    io.observe(blockEl);
    return () => { io.disconnect(); setNeedsYouInView(false); };
  }, [blockEl]);
  const shown = useMemo(() => {
    const list = filter === 'coach' ? mergeCoachLog(coachLog || [], items) : filterItems(items, 'all');
    const out = new Set([pinned && String(pinned.id), ...needsYou.map((i) => String(i.id))].filter(Boolean));
    return list.filter((i) => !out.has(String(i.id)));
  }, [filter, coachLog, items, pinned, needsYou]);
  const rows = useMemo(() => withDayDividers(shown, today), [shown, today]);
  const working = runs.length > 0 || Boolean(creation && !creation.done) || rebuilding;
  const quiet = filter === 'all' && isQuiet(items, { now, working, sending: sending.length > 0 });

  useEffect(() => { setAcked(readJSON(ACK_KEY, [])); }, [ACK_KEY]);
  useEffect(() => {
    setReadSeq(readJSON(READ_KEY, null));
    // Read in another tab: this tab's badge clears too.
    const onStorage = (e) => { if (e.key === READ_KEY) setReadSeq(readJSON(READ_KEY, null)); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [READ_KEY]);
  // The tab title carries the badge ("(1) aithlo") while the panel is closed.
  const baseTitle = useRef(typeof document === 'undefined' ? 'aithlo' : document.title);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.title = badgedTitle(baseTitle.current, isOpen ? 0 : badge);
    return () => { document.title = baseTitle.current; };
  }, [badge, isOpen]);
  // Onboarding's hand-off (handOffToCoach): open on arrival, the first plan's
  // working item showing as soon as there is a client (a late /v2/me, a new client).
  useEffect(() => {
    const { open, buildSince } = readArrival(readOnly ? null : client);
    if (open) setIsOpen(true);
    if (buildSince != null) client?.expectBuild?.(buildSince);
  }, [client, readOnly]);
  // The athlete closing the panel ends the hand-off: a later mount starts closed.
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (wasOpen.current && !isOpen && !embedded) clearArrival();
    wasOpen.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    if (embedded) return;
    try { localStorage.setItem(VIEW_KEY, view); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('chat:layout', { detail: { open: isOpen, view, rail: true } }));
  }, [isOpen, view, embedded]);
  // Unmounted: the shell drops the rail's gutter too.
  useEffect(() => () => {
    if (!embedded) window.dispatchEvent(new CustomEvent('chat:layout', { detail: { open: false, rail: false } }));
  }, [embedded]);

  useEffect(() => { if (mode !== 'ai' && !humanCoaches.find((c) => c.id === mode)) setMode('ai'); }, [humanCoaches, mode]);

  const askAbout = (target) => {
    if (!target?.ref || readOnly) return;
    setMode('ai');
    setIsOpen(true);
    setFilter('all');
    setContext({ ref: String(target.ref), label: target.label || 'this' });
  };
  const askAboutItem = (item) => askAbout({ ref: item.id, label: contextLabel(item) });

  useEffect(() => {
    // `dock` (the nav's Coach): beside the page, whichever way the panel was last left.
    const open = (e) => { setIsOpen(true); if (e?.detail?.dock) setView('sidebar'); };
    const prefill = (e) => { if (e?.detail?.text && !readOnly) { setIsOpen(true); setMode('ai'); setInput(e.detail.text); } };
    // Ask why from outside the thread (a session's detail): { context_ref, label }.
    const why = (e) => { if (e?.detail?.context_ref) askAbout({ ref: e.detail.context_ref, label: e.detail.label }); };
    // Review from outside the thread (the Plan's "Review the proposed plan", a
    // proposal banner) or the bar: open on that card (else the first waiting), highlighted.
    const review = (e) => {
      const asked = e?.detail?.item_id != null ? String(e.detail.item_id) : null;
      const list = waitingRef.current;
      const id = (asked && list.some((i) => String(i.id) === asked) ? asked : null) || (list[0] ? String(list[0].id) : asked);
      setIsOpen(true);
      setMode('ai');
      setFilter('all');
      if (id) setFocus({ id, smooth: true, highlight: true, n: Date.now() });
    };
    window.addEventListener('chat:open', open);
    window.addEventListener('chat:prefill', prefill);
    window.addEventListener('chat:why', why);
    window.addEventListener(REVIEW_EVENT, review);
    return () => {
      window.removeEventListener(REVIEW_EVENT, review);
      window.removeEventListener('chat:open', open);
      window.removeEventListener('chat:prefill', prefill);
      window.removeEventListener('chat:why', why);
    };
  }, [client, readOnly]);

  // The end of the thread: the Needs-you block when something waits (its top,
  // when it is taller than the list), else the last item.
  const toEnd = () => {
    const list = listRef.current;
    const block = list?.querySelector('[data-needs-you]');
    if (block && block.offsetHeight > list.clientHeight) block.scrollIntoView({ block: 'start' });
    else endRef.current?.scrollIntoView({ block: 'end' });
  };
  // Opening: at the bottom when a card waits for the athlete (the Needs-you
  // block is there), else on the first unread item, else at the bottom; once per opening.
  const highlighted = useRef(null);
  useEffect(() => {
    if (!isOpen) { setAnchor(null); setFocus(null); highlighted.current = null; return; }
    setAnchor(firstUnreadId(items, readSeq));
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    if (focus && mode === 'ai') {
      // Once per ask: a re-run (the opening's anchor landing) leaves a smooth scroll alone.
      const again = highlighted.current === focus.n;
      highlighted.current = focus.n;
      const hit = again ? listRef.current?.querySelector(`[data-item-id="${CSS.escape(focus.id)}"]`)
        : focusThreadItem(listRef.current, focus.id, { smooth: focus.smooth, highlight: focus.highlight });
      // A card in the Needs-you block is at the bottom; one up in the history is being read.
      if (hit) { atBottom.current = Boolean(hit.closest?.('[data-needs-you]')); return; }
    }
    const el = !waitingRef.current.length && anchor && listRef.current?.querySelector(`[data-item-id="${CSS.escape(anchor)}"]`);
    if (el) el.scrollIntoView({ block: 'start' });
    else toEnd();
    atBottom.current = !el;               // opened on an unread item: reading, not at the end
  }, [isOpen, anchor, focus, mode]);
  // While open: follow what arrives, and remember how far the athlete has read.
  const lastSeq = items.length ? items[items.length - 1].seq : null;
  useEffect(() => {
    if (!isOpen || mode !== 'ai' || lastSeq == null || readOnly) return;
    writeJSON(READ_KEY, lastSeq);
    setReadSeq(lastSeq);
  }, [isOpen, mode, lastSeq, READ_KEY, readOnly]);
  // The chips: read on opening and after each new item (a reply, a race landing). They are a nicety, so a failure is no chips.
  useEffect(() => {
    if (!isOpen || readOnly || !chatOpen) return undefined;
    let cancelled = false;
    Promise.resolve(api.suggestions?.())
      .then((r) => { if (!cancelled) setSuggestions(readSuggestions(r?.data)); })
      .catch(() => { if (!cancelled) setSuggestions([]); });
    return () => { cancelled = true; };
  }, [isOpen, readOnly, chatOpen, lastSeq, api, activitiesSeen]);
  // An activity removed (here or on any page), merged or restored: the chips may
  // name it, and the activity it shows or the next message asks about may be gone.
  useEffect(() => onActivitiesChanged((detail) => {
    const { removed = [] } = detail;
    setActivitiesSeen((n) => n + 1);
    setRemovedActivities((known) => knownRemoved(known, detail));
    if (!removed.length) return;
    setActivity((open) => (open != null && removed.includes(String(open)) ? null : open));
    setContext((c) => (c && aboutRemovedActivity(c.ref, removed) ? null : c));
    setSuggestions((list) => list.filter((c) => !aboutRemovedActivity(c.context_ref, removed)));
  }), []);
  const follow = [items.length, runs.length, sending.length, runs.map(([, r]) => `${(r.text || '').length}.${r.steps?.length || 0}`).join(),
    creation?.steps.length, creation?.insights.length].join('|');
  const firstFollow = useRef(true);
  // Follow only an athlete already at the bottom: someone scrolled up to read
  // an older card isn't yanked away by every new item (#69). Their own send
  // always follows.
  const atBottom = useRef(true);
  const lastSending = useRef(sending.length);
  const onListScroll = (e) => {
    const el = e.currentTarget;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  useEffect(() => {
    const sent = sending.length > lastSending.current;
    lastSending.current = sending.length;
    if (!isOpen) { firstFollow.current = true; atBottom.current = true; return; }
    if (firstFollow.current) { firstFollow.current = false; return; }     // the opening scroll stands
    if (filter === 'all' && (atBottom.current || sent)) toEnd();
  }, [isOpen, follow]);
  // Back to All from Coach actions: the newest messages, not the top of the conversation.
  const lastFilter = useRef(filter);
  useEffect(() => {
    const back = lastFilter.current !== 'all' && filter === 'all';
    lastFilter.current = filter;
    if (back) { atBottom.current = true; toEnd(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  // A new card waiting while the panel is open (it lands in the Needs-you
  // block at the bottom): scrolled to for an athlete at the bottom and not
  // typing; anyone else (reading history, mid-sentence) isn't moved, the bar
  // ("Review ↓") says so instead.
  const seenWaiting = useRef(null);
  const waitingKey = waiting.map((i) => String(i.id)).join('|');
  useEffect(() => {
    const ids = waitingKey ? waitingKey.split('|') : [];
    const prev = seenWaiting.current;
    seenWaiting.current = new Set(ids);
    if (!isOpen || prev == null) return;                // the opening takes care of what was already there
    const fresh = ids.find((id) => !prev.has(id));
    if (!fresh) return;
    const typing = input.trim() !== '' || (typeof document !== 'undefined' && document.activeElement === inputRef.current);
    if (filter === 'all' && mode === 'ai' && atBottom.current && !typing) setFocus({ id: fresh, smooth: true, highlight: true, n: Date.now() });
    else setNudge(Date.now());
  }, [waitingKey, isOpen]);

  useEffect(() => {
    if (!isOpen || embedded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, embedded]);
  useEffect(() => {
    if (!context) return undefined;
    inputRef.current?.focus();
    // Again once a closing dialog (the session detail) has handed focus back.
    const t = setTimeout(() => inputRef.current?.focus(), 250);
    return () => clearTimeout(t);
  }, [context]);

  // The coach log: read when the filter is chosen (and again on each choice).
  useEffect(() => {
    if (filter !== 'coach' || !isOpen) return undefined;
    let cancelled = false;
    setLogError(false);
    api.thread(null, 'coach')
      .then(({ data }) => { if (!cancelled) setCoachLog(data?.items || []); })
      .catch(() => { if (!cancelled) setLogError(true); });
    return () => { cancelled = true; };
  }, [filter, isOpen, api]);

  const sendText = (text, ref = context?.ref || null, chip = null) => {
    if (!text || !client || readOnly) return;
    client.send(commands.message(text, ref, chip));
    setContext(null);
    setFilter('all');
  };
  const sendChip = (c) => sendText(c.text, c.context_ref, c.state);
  const submit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    sendText(text);
    setInput('');
  };
  const acknowledge = (item) => {
    const next = [...new Set([...acked, String(item.id)])];
    setAcked(next);
    writeJSON(ACK_KEY, next);
  };
  const status = statusLine(Object.fromEntries(runs)) || (creation && !creation.done ? creation.steps.at(-1)?.label : null)
    || (starting ? starting.steps.at(-1)?.label : null) || (rebuilding ? REBUILDING_TEXT : null);
  const onAddRace = () => {
    try { sessionStorage.setItem(ADD_RACE_KEY, '1'); } catch { /* the calendar's add tile is still there */ }
    window.dispatchEvent(new CustomEvent('calendar:add-race'));
    navigate('/calendar');
  };
  // A phase review's Open timeline: the race screen, with the panel out of its way where it covers the page.
  const openTimeline = () => {
    navigate('/season');
    if (wide || window.matchMedia?.('(max-width: 1023px)')?.matches) setIsOpen(false);
  };
  // What a reply names, opened: an activity over the panel, a session on the calendar, a race on its screen.
  const openObject = ({ kind, id }) => {
    if (kind === 'activity') { if (!removedActivities.has(String(id))) setActivity(id); return; }
    if (kind === 'race') { openTimeline(); return; }
    if (kind === 'workout') navigate('/workouts');
    if (kind === 'session') {
      try { sessionStorage.setItem(OPEN_SESSION_KEY, String(id)); } catch { /* the calendar still opens */ }
      window.dispatchEvent(new CustomEvent(OPEN_SESSION_EVENT));
      navigate('/calendar');
    }
    if (wide || window.matchMedia?.('(max-width: 1023px)')?.matches) setIsOpen(false);
  };
  // What an athlete message asked about: a thread item, else a session on the calendar.
  const about = (ref) => {
    const named = objectLabel(ref, state.cal);
    if (named) return named;
    const item = state.cal?.thread?.[String(ref)];
    if (item) return contextLabel(item);
    const session = state.cal?.sessions?.[String(ref)];
    return session ? contextLabel(session) : null;
  };

  const humanUnread = humanCoaches.length ? coachUnreadTotal : 0;
  if (!isOpen) {
    const label = badge ? `Open coach, ${badge} new from your coach` : humanUnread ? `Open coach, ${humanUnread} unread from your coach` : 'Open coach';
    const count = badge > 0 ? badge : humanUnread;
    const dot = count > 0 && (
      <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-bg-panel bg-status-danger px-1 text-[11px] font-bold text-white"
        {...(badge > 0 ? { 'aria-label': `${badge} new from your coach` } : { 'aria-hidden': 'true' })}>
        {count > 9 ? '9+' : count}
      </span>
    );
    const pulse = working && (
      <span className="absolute -bottom-0.5 -left-0.5 flex h-3.5 w-3.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-default opacity-75" />
        <span className="relative inline-flex h-3.5 w-3.5 rounded-full border border-bg-panel bg-accent-default" />
      </span>
    );
    return (<>
      {/* Desktop: the collapsed coach is a 56px rail on the right (Figma "Web coach rail"). */}
      <div className="fixed inset-y-0 right-0 z-[9997] hidden w-14 flex-col items-center border-l border-border-subtle bg-bg-panel pt-5 lg:flex">
        <button type="button" onClick={() => setIsOpen(true)} aria-label={label}
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-accent-tint-border bg-accent-tint text-accent-default transition-colors hover:border-accent-default">
          <Sparkle size={18} className="fill-current" aria-hidden="true" />
          {dot}{pulse}
        </button>
      </div>
      {/* Tablet: the floating button. A phone has Coach in its bottom bar, and the
          button sat over the page and the bar's More menu (Sidebar.jsx). */}
      <button type="button" onClick={() => setIsOpen(true)} aria-label={label}
        className="fixed bottom-6 right-6 z-[9999] hidden md:flex h-14 w-14 items-center justify-center rounded-full bg-accent-default text-accent-on shadow-lg transition-transform hover:scale-105 lg:hidden">
        <MessageCircle size={24} />
        {dot}{pulse}
      </button>
    </>);
  }

  const latestCoach = sending.length ? null : latestCoachId(items);
  const followups = latestCoach ? followupChips(state.cal?.thread?.[latestCoach]) : [];
  const whoLabel = readOnly ? (viewingAthlete?.username ? `${viewingAthlete.username}'s coach` : "The athlete's coach") : 'Aithlo coach';
  const subtitle = activeCoach ? 'Your coach'
    : status || (readOnly ? `${whoLabel}, read-only` : 'Your coach');
  const itemProps = {
    onAct: act, pending: state.pending, work: state.cal?.work, races, onWhy: askAboutItem, onTalk: askAboutItem,
    onOpenTimeline: openTimeline, onAddRace: onAddRace, about, now, today, readOnly, outcomes: state.outcomes,
    latestCoach, onAsk: chatOpen ? sendChip : null, onOpen: openObject, thread: state.cal?.thread, removedActivities,
  };
  // Nothing to tap while the coach is answering, or over something the athlete has started to write.
  const chips = !readOnly && chatOpen && filter === 'all' && !working && !sending.length && !input.trim() && !context
    ? composerChips({ suggestions, quiet, cal: state.cal, today, shown: followups }) : [];
  const empty = filter === 'all' && items.length === 0 && sending.length === 0 && runs.length === 0 && !creation && !starting;

  const filterControl = !activeCoach && (
    <SegmentedControl label="Show" value={filter} onChange={setFilter} className="shrink-0 p-0.5"
      segments={FILTERS.map(([value, label]) => ({ value, label: wide && value === 'coach' ? 'Coach actions' : label }))} />
  );
  const clearButton = !activeCoach && !readOnly && filter === 'all' && canClearChat(items) && (
    <button type="button" onClick={clearChat} title="Clear chat" aria-label="Clear chat"
      className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-text-secondary hover:bg-bg-raised hover:text-text-primary', FOCUS)}>
      <Eraser size={15} aria-hidden="true" />
    </button>
  );
  const whoChips = (
    <div className="flex min-w-0 gap-1.5 overflow-x-auto"
      {...(humanCoaches.length > 0 ? { role: 'group', 'aria-label': "Who you're talking to" } : {})}>
      <Chip selected={mode === 'ai'} onClick={() => setMode('ai')}>{whoLabel}</Chip>
      {humanCoaches.map((c) => (
        <Chip key={c.id} selected={mode === c.id} onClick={() => setMode(c.id)}>
          {`@${c.username}`}{c.unread_count > 0 && mode !== c.id ? ` · ${c.unread_count}` : ''}
        </Chip>
      ))}
    </div>
  );

  return (
    <div className={cn('flex flex-col overflow-hidden font-body', embedded ? 'h-full min-h-0 bg-bg-panel' : cn('fixed z-[9998]',
      // Expanded (Figma 55:5428): the coach as a page beside the app's sidebar, not over it.
      wide ? cn('inset-0 bg-bg-canvas pb-(--safe-bottom) pt-(--safe-top)', sidebarCollapsed ? 'md:left-16' : 'md:left-[232px]')
        : 'inset-0 bg-bg-panel pb-(--safe-bottom) pt-(--safe-top) shadow-2xl lg:pb-0 lg:pt-0 lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[var(--coach-width,380px)] lg:border-l lg:border-border-subtle'))}
      id="coach-panel" role={embedded ? 'region' : 'dialog'} aria-modal={embedded ? undefined : 'true'} aria-label="Aithlo coach">
      {!embedded && !wide && <CoachResize />}
      {activity != null && (
        <Suspense fallback={null}>
          <ActivityDetailModal activityId={activity} onClose={() => setActivity(null)}
            onMissing={(id) => setRemovedActivities((known) => knownRemoved(known, { kind: 'removed', removed: [id] }))} />
        </Suspense>
      )}
      {wide ? (<>
      {/* ✦ Coach, the filter and Dock (Figma "Web · Coach expanded"). */}
      <div className="mx-auto flex w-full max-w-[808px] shrink-0 flex-wrap items-center gap-x-2.5 gap-y-3 px-4 pb-4 pt-7">
        <Sparkle size={20} className="shrink-0 fill-current text-accent-default" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 className="m-0 truncate text-[28px] font-semibold leading-[1.2] text-text-primary">{activeCoach ? `@${activeCoach.username}` : 'Coach'}</h2>
          {(status || readOnly || activeCoach) && (
            <p className="m-0 flex items-center gap-1.5 truncate type-caption text-text-tertiary" aria-live="polite">
              {!activeCoach && status && <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent-default" />}
              {subtitle}
            </p>
          )}
        </div>
        {filterControl}
        {clearButton}
        <Button size="small" kind="secondary" className="hidden lg:inline-flex" onClick={() => setView('sidebar')}
          aria-label="Dock the coach to the side" icon={<ArrowRightToLine size={14} aria-hidden="true" />}>Dock</Button>
        <button type="button" onClick={() => setIsOpen(false)} title="Close" aria-label="Close coach"
          className={cn('flex h-8 w-8 items-center justify-center rounded-full text-text-secondary hover:bg-bg-raised hover:text-text-primary lg:hidden', FOCUS)}>
          <X size={18} />
        </button>
      </div>
      {(humanCoaches.length > 0 || readOnly) && (
        <div className="mx-auto flex w-full max-w-[808px] shrink-0 px-4 pb-3">{whoChips}</div>
      )}
      </>) : (<>
      {/* ✦ Coach (Figma "Coach panel · web"): who's talking and what it's doing. */}
      <div className="flex shrink-0 items-center gap-2 pb-3.5 pl-5 pr-4 pt-[18px]">
        <Sparkle size={16} className="shrink-0 fill-current text-accent-default" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h3 className="m-0 truncate text-[17px] font-semibold leading-[1.3] text-text-primary">{activeCoach ? `@${activeCoach.username}` : 'Coach'}</h3>
          <p className="m-0 flex items-center gap-1.5 truncate type-caption text-text-tertiary" aria-live="polite">
            {!activeCoach && status && <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent-default" />}
            {subtitle}
          </p>
        </div>
        {clearButton}
        {!embedded && (<>
        <button type="button" onClick={() => setView('fullscreen')} title="Expand" aria-label="Expand the coach"
          className={cn('hidden h-8 w-8 items-center justify-center rounded-full text-text-secondary hover:bg-bg-raised hover:text-text-primary lg:flex', FOCUS)}>
          <Maximize2 size={15} />
        </button>
        <button type="button" onClick={() => setIsOpen(false)} title="Close" aria-label="Close coach"
          className={cn('flex h-8 w-8 items-center justify-center rounded-full text-text-secondary hover:bg-bg-raised hover:text-text-primary', FOCUS)}>
          <X size={18} className="lg:hidden" /><ChevronRight size={18} className="hidden lg:block" />
        </button>
        </>)}
      </div>
      {/* Who you're talking to (the AI coach, a human coach), then All / Actions. */}
      <div className="flex shrink-0 items-center gap-1.5 px-4 pb-2.5">
        {whoChips}
        <span className="flex-1" />
        {filterControl}
      </div>
      <div className="h-px shrink-0 bg-border-subtle" />
      </>)}
      {activeCoach ? (
        <HumanCoachThread coach={activeCoach} isOpen={isOpen} onRead={fetchRelationships} inputRef={inputRef} wide={wide} />
      ) : (<>
      <div ref={listRef} onScroll={onListScroll} className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
      <div className={cn('space-y-3 p-4', wide && 'mx-auto w-full max-w-[808px] pt-1')}>
        <SyncStatus primary />
        {pinned && <PinnedSafety key={pinned.id} item={pinned} seenKey={SEEN_KEY} onTalk={askAboutItem} onAcknowledge={acknowledge} readOnly={readOnly} />}
        {empty && (readOnly ? (
          <p className="m-0 pt-10 text-center type-body-small text-text-tertiary">Nothing in the coach thread yet.</p>
        ) : (
          <div className="pt-24">
            {chatOpen
              ? <EmptyState title="Ask your coach anything" body="About today, your week, a niggle or a race." />
              : <EmptyState title="Your coach's plans and updates show here." />}
          </div>
        ))}
        {filter === 'coach' && shown.length === 0 && (
          <p className="m-0 pt-10 text-center type-body-small text-text-tertiary">
            {logError ? "Couldn't load the coach log." : coachLog == null ? 'Loading…' : 'Nothing the coach has done yet.'}
          </p>
        )}
        {rows.map((row) => (row.kind === 'day' ? (
          <p key={row.key} className="m-0 pt-1 text-center type-caption text-text-tertiary" role="separator" aria-label={row.label}>
            {row.label}
          </p>
        ) : (
          <div key={row.key} data-item-id={row.key}>
            {anchor === row.key && (
              <p className="m-0 mb-2 text-center type-eyebrow uppercase text-accent-default">New</p>
            )}
            {filter === 'coach' ? (
              <CoachActionRow item={row.item}>
                <ThreadItem item={row.item} busy={acting.has(row.key)} {...itemProps} />
              </CoachActionRow>
            ) : (
              <ThreadItem item={row.item} busy={acting.has(row.key)} {...itemProps} />
            )}
          </div>
        )))}
        {filter === 'all' && creation && !creation.done && <CreationWork view={creation} />}
        {filter === 'all' && starting && <CreationWork view={starting} />}
        {filter === 'all' && sending.map((c) => (
          <AthleteText key={c.key} text={c.op.text} pending={c.status === 'queued' ? 'queued' : 'sending'}
            about={c.op.context_ref ? about(c.op.context_ref) : null} />
        ))}
        {filter === 'all' && unsent.map((f) => (
          <AthleteText key={f.key} text={f.op.text} about={f.op.context_ref ? about(f.op.context_ref) : null}
            failed={{ onRetry: canRetry(f) ? () => client?.retry(f.key) : null, onRemove: () => client?.dismissFailure(f.key) }} />
        ))}
        {filter === 'all' && runs.map(([run, r]) => {
          if (r.steps?.length) {
            return (
              <div key={run} className="space-y-2">
                <StepList steps={r.steps} working={!r.text} />
                {r.text && <CoachText text={r.text} streaming />}
              </div>
            );
          }
          return r.text ? <CoachText key={run} text={r.text} streaming /> : (
            <p key={run} className="m-0 flex items-center gap-2 type-caption text-text-tertiary" role="status">
              <span className="flex gap-0.5" aria-hidden="true">
                {[0, 1, 2].map((d) => (
                  <span key={d} className="h-1 w-1 animate-pulse rounded-full bg-text-tertiary" style={{ animationDelay: `${d * 150}ms` }} />
                ))}
              </span>
              {r.label || 'Working…'}
            </p>
          );
        })}
        {needsYou.length > 0 && (
          <section ref={setBlockEl} aria-label="Needs you" data-needs-you="" className="space-y-3">
            {(wide || needsYou.length > 1) && <p className="m-0 type-eyebrow uppercase text-accent-default">{needsYou.length > 1 ? `Needs you · ${needsYou.length}` : 'Needs you'}</p>}
            {needsYou.map((item) => (
              <div key={String(item.id)} data-item-id={String(item.id)}>
                <ThreadItem item={item} busy={acting.has(String(item.id))} {...itemProps} />
              </div>
            ))}
          </section>
        )}
        <div ref={endRef} />
      </div>
      </div>
      {!readOnly && waiting.length > 0 && (
        <WaitingBar waiting={waiting} wide={wide} nudge={nudge} inView={needsYouInView}
          onReview={() => {
            setFilter('all');
            setFocus({ id: String(waiting[0].id), smooth: true, highlight: true, n: Date.now() });
          }} />
      )}
      <SuggestionChips chips={chips} onPick={sendChip} disabled={!client}
        className={cn('shrink-0 px-4 pt-2', wide && 'mx-auto w-full max-w-[808px]')} />
      {readOnly ? (
        <p className={cn('m-0 flex shrink-0 items-center justify-center gap-1.5 px-4 pb-4 pt-3 text-center type-caption text-text-tertiary', wide ? 'mx-auto w-full max-w-[808px]' : 'border-t border-border-subtle')}
          role="note">
          <Eye size={13} aria-hidden="true" /> You're viewing this athlete's coach thread. It's read-only.
        </p>
      ) : (
        <Composer value={input} onChange={setInput} onSubmit={submit} disabled={!client} allowed={chatOpen}
          inputRef={inputRef} wide={wide} context={context} onClearContext={() => setContext(null)}
          placeholder={composerPlaceholder(context, suggestions)} />
      )}
      </>)}
    </div>
  );
}
